/**
 * Minimal DatoCMS Content Delivery API client for the blog.
 *
 * Blog content lives in the DatoCMS `blogPost` model and is fetched via its
 * GraphQL CDA. We use a plain fetch wrapper instead of a GraphQL client library;
 * queries are colocated here with the data helpers the blog pages use.
 *
 * Auth: DATOCMS_CDA_TOKEN. Read at build time only; never shipped to the browser.
 *
 * The `blogPost` model fields used: title, slug, description (excerpt source),
 * content (Structured Text body + blocks), featuredImage, authors, categories,
 * tags.
 */
import { render as renderPlainText } from 'datocms-structured-text-to-plain-text';

// Prefer IPv4 when resolving hosts. Node's fetch (undici) can throw
// "fetch failed" when a host advertises AAAA records but the machine's IPv6
// route is broken — which broke build-time DatoCMS fetches in the export
// workers. This module is server-only, so the dns import never reaches the
// browser; the guard is belt-and-suspenders.
if (typeof window === 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  require('dns').setDefaultResultOrder('ipv4first');
}

const DATOCMS_ENDPOINT = 'https://graphql.datocms.com/';
const REQUEST_TIMEOUT_MS = 10_000;
const MAX_RATE_LIMIT_RETRIES = 4;

// DatoCMS page size for list queries; keep requesting pages until the final one.
export const POSTS_LIMIT = 100;

export async function datoRequest<T = unknown>(
  query: string,
  variables: Record<string, unknown> = {}
): Promise<T> {
  const token = process.env.DATOCMS_CDA_TOKEN;
  if (!token) {
    throw new Error('DatoCMS CDA token is not set');
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
  const endpoint = `${DATOCMS_ENDPOINT}?_fresh=${Date.now()}`;
  let res: Response;
  for (let attempt = 0; ; attempt++) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      res = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({ query, variables }),
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timeout);
    }

    if (res.status !== 429 || attempt >= MAX_RATE_LIMIT_RETRIES) break;

    const retryAfter = res.headers.get('Retry-After');
    let retryDelay = NaN;
    if (retryAfter) {
      const seconds = Number(retryAfter);
      retryDelay = Number.isFinite(seconds)
        ? seconds * 1000
        : Date.parse(retryAfter) - Date.now();
    }
    const delay = Number.isFinite(retryDelay)
      ? retryDelay
      : 1000 * 2 ** attempt + Math.random() * 250;
    await new Promise((resolve) => setTimeout(resolve, Math.max(0, delay)));
  }

  if (!res.ok) {
    throw new Error(`DatoCMS request failed: ${res.status} ${res.statusText}`);
  }

  const json = await res.json();

  if (json.errors) {
    throw new Error(`DatoCMS query failed: ${JSON.stringify(json.errors)}`);
  }

  return json.data as T;
}

/** react-datocms <Image /> responsiveImage fields. */
const RESPONSIVE_IMAGE = `
  src
  srcSet
  sizes
  width
  height
  alt
  title
  base64
  aspectRatio
`;

/**
 * A category or tag as returned by the CDA. Only the display name is selected —
 * neither taxonomy is browsable (there is no /blog/tag/[slug] route), so the
 * models' slugs are never queried.
 */
export interface BlogTaxonomy {
  name: string | null;
}

export interface BlogAuthor {
  name: string | null;
  // react-datocms responsiveImage (or null) — typed loosely on purpose.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  avatar: any | null;
}

export interface BlogPostListItem {
  id: string;
  title: string | null;
  slug: string | null;
  firstPublishedAt: string | null;
  updatedAt: string | null;
  excerpt: string;
  // react-datocms responsiveImage of the cover image, or null.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  coverImage: any | null;
  externalImageUrl: string | null;
  externalImageWidth: number | null;
  externalImageHeight: number | null;
  category: string | null;
  // Intended reader ("Students" | "Tutors" | "Both"). Used purely for
  // client-side segregation — never rendered on the blog itself.
  audience: string | null;
}

/** The two reader segments the blog can be filtered by. */
export type AudienceFilter = 'students' | 'tutors';

/**
 * Whether a post belongs in the given audience's section. A post tagged "Both"
 * appears in both; an untagged post belongs to neither (so it never shows up
 * under two headings at once — callers decide where untagged posts go).
 */
export function matchesAudience(
  audience: string | null,
  filter: AudienceFilter
): boolean {
  const a = (audience ?? '').toLowerCase();
  if (a === 'both') return true;
  return a === filter;
}

export interface BlogPost {
  id: string;
  title: string | null;
  slug: string | null;
  firstPublishedAt: string | null;
  updatedAt: string | null;
  description: string;
  // Shape expected by react-datocms <StructuredText />: { value, blocks, links }.
  // Typed loosely to avoid coupling to a specific datocms-structured-text version.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  structuredText: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  coverImage: any | null;
  externalImageUrl: string | null;
  externalImageWidth: number | null;
  externalImageHeight: number | null;
  authors: BlogAuthor[];
  categories: BlogTaxonomy[];
  tags: BlogTaxonomy[];
  /** Plain-text Q&A pulled from the post's `FaqRecord` blocks, for FAQPage JSON-LD. */
  faqs: { question: string; answer: string }[];
}

/** Builds a short plain-text excerpt from a Structured Text field. */
function buildExcerpt(structuredText: unknown, max = 180): string {
  try {
    const text = renderPlainText(structuredText as never) || '';
    if (text.length <= max) return text;
    return `${text.slice(0, max).replace(/\s+\S*$/, '')}…`;
  } catch {
    return '';
  }
}

/**
 * Extract plain-text Q&A pairs from a post's Structured Text `FaqRecord`
 * blocks. `question`/`answer` are themselves Structured Text ({ value }), so
 * we flatten each to plain text — the shape FAQPage JSON-LD needs. Blocks
 * missing either side are skipped.
 */
/** Minimal local shape of a Structured Text value field (only `value`). */
type DatoStructuredTextField = { value: unknown };

/** Minimal local shape of a Structured Text block (only the fields read here). */
interface DatoStructuredTextBlock {
  __typename: string;
  question?: DatoStructuredTextField;
  answer?: DatoStructuredTextField;
}

/** Minimal local shape of a DatoCMS Structured Text value (value + blocks). */
interface DatoStructuredText {
  value?: unknown;
  blocks?: DatoStructuredTextBlock[];
}

function extractFaqs(
  structuredText: unknown
): { question: string; answer: string }[] {
  const blocks: DatoStructuredTextBlock[] =
    (structuredText as DatoStructuredText | null | undefined)?.blocks ?? [];
  return blocks
    .filter((b): b is DatoStructuredTextBlock => b?.__typename === 'FaqRecord')
    .map((b) => ({
      question: (renderPlainText(b.question) || '').trim(),
      answer: (renderPlainText(b.answer) || '').trim(),
    }))
    .filter((f) => f.question && f.answer);
}

/** All posts, newest first, for the blog index. */
export async function getAllPosts(): Promise<BlogPostListItem[]> {
  const posts: BlogPostListItem[] = [];
  for (let skip = 0; ; skip += POSTS_LIMIT) {
    const data = await datoRequest<{
      allBlogPosts: Array<{
        id: string;
        title: string | null;
        slug: string | null;
        _firstPublishedAt: string | null;
        _updatedAt: string | null;
        description: unknown;
        featuredImage: { responsiveImage: unknown } | null;
        externalImageUrl: string | null;
        externalImageWidth: number | null;
        externalImageHeight: number | null;
        categories: Array<{ category: string | null }>;
        audience: string | null;
      }>;
    }>(
      `
    query AllPosts($skip: IntType!) {
      allBlogPosts(orderBy: [_firstPublishedAt_DESC, id_ASC], first: ${POSTS_LIMIT}, skip: $skip) {
        id
        title
        slug
        _firstPublishedAt
        _updatedAt
        description {
          value
        }
        featuredImage {
          responsiveImage(imgixParams: { w: 800, fit: crop, ar: "16:9", auto: format }) {
            ${RESPONSIVE_IMAGE}
          }
        }
        externalImageUrl
        externalImageWidth
        externalImageHeight
        categories {
          category
        }
        audience
      }
    }
  `,
      { skip }
    );

    posts.push(
      ...data.allBlogPosts.map((post) => ({
        id: post.id,
        title: post.title,
        slug: post.slug,
        firstPublishedAt: post._firstPublishedAt,
        updatedAt: post._updatedAt,
        excerpt: buildExcerpt(post.description),
        coverImage: post.featuredImage?.responsiveImage ?? null,
        externalImageUrl: post.externalImageUrl ?? null,
        externalImageWidth: post.externalImageWidth ?? null,
        externalImageHeight: post.externalImageHeight ?? null,
        category: post.categories?.[0]?.category ?? null,
        audience: post.audience ?? null,
      }))
    );
    if (data.allBlogPosts.length < POSTS_LIMIT) break;
  }
  return posts;
}

/** Slugs of all published posts. */
export async function getAllPostSlugs(): Promise<string[]> {
  const slugs: string[] = [];
  for (let skip = 0; ; skip += POSTS_LIMIT) {
    const data = await datoRequest<{
      allBlogPosts: Array<{ slug: string | null }>;
    }>(
      `
    query AllPostSlugs($skip: IntType!) {
      allBlogPosts(orderBy: id_ASC, first: ${POSTS_LIMIT}, skip: $skip) {
        slug
      }
    }
  `,
      { skip }
    );
    slugs.push(
      ...data.allBlogPosts
        .map((post) => post.slug)
        .filter((slug): slug is string => Boolean(slug))
    );
    if (data.allBlogPosts.length < POSTS_LIMIT) break;
  }
  return slugs;
}

/** A single post by slug, with full Structured Text + embedded blocks. */
export async function getPostBySlug(slug: string): Promise<BlogPost | null> {
  const data = await datoRequest<{
    blogPost: {
      id: string;
      title: string | null;
      slug: string | null;
      firstPublishedAt: string | null;
      updatedAt: string | null;
      description: unknown;
      structuredText: unknown;
      featuredImage: { responsiveImage: unknown } | null;
      externalImageUrl: string | null;
      externalImageWidth: number | null;
      externalImageHeight: number | null;
      authors: Array<{
        name: string | null;
        avatar: { responsiveImage: unknown } | null;
      }>;
      categories: Array<{ name: string | null }>;
      tags: Array<{ name: string | null }>;
    } | null;
  }>(
    `
    query PostBySlug($slug: String!) {
      blogPost(filter: { slug: { eq: $slug } }) {
        id
        title
        slug
        firstPublishedAt: _firstPublishedAt
        updatedAt: _updatedAt
        description {
          value
        }
        structuredText: content {
          value
          blocks {
            __typename
            ... on RecordInterface {
              id
            }
            ... on ImageRecord {
              image {
                title
                alt
                responsiveImage(imgixParams: { w: 1200, fit: max, auto: format }) {
                  ${RESPONSIVE_IMAGE}
                }
              }
            }
            ... on VideoRecord {
              video {
                url
                title
                video {
                  streamingUrl
                  mp4Url
                  thumbnailUrl
                }
              }
            }
            ... on ExternalVideoRecord {
              externalVideo {
                url
                title
                width
                height
                provider
                providerUid
                thumbnailUrl
              }
            }
            ... on StatisticRecord {
              stat
              description
            }
            ... on FaqRecord {
              question {
                value
              }
              answer {
                value
              }
            }
            ... on ButtonRecord {
              label
              externalUrl
              isInternalLink
              buttonStyle
            }
            ... on CtaRecord {
              title
              description {
                value
              }
              button {
                label
                externalUrl
              }
            }
            ... on TestimonialRecord {
              quote {
                value
              }
              person {
                name
              }
            }
            ... on MediaGalleryRecord {
              gallery {
                title
                responsiveImage(imgixParams: { w: 600, fit: crop, ar: "1:1", auto: format }) {
                  ${RESPONSIVE_IMAGE}
                }
              }
            }
          }
          inlineBlocks {
            __typename
            ... on RecordInterface {
              id
            }
            ... on ImageRecord {
              image {
                responsiveImage(imgixParams: { w: 400, auto: format }) {
                  ${RESPONSIVE_IMAGE}
                }
              }
            }
            ... on ButtonRecord {
              label
              externalUrl
            }
            ... on SocialLinkRecord {
              platform
              url
            }
          }
          links {
            __typename
            ... on RecordInterface {
              id
            }
            ... on BlogPostRecord {
              slug
              title
            }
            ... on AuthorRecord {
              name
            }
            ... on CategoryRecord {
              name: category
            }
            ... on TagRecord {
              name: tag
            }
          }
        }
        featuredImage {
          responsiveImage(imgixParams: { w: 1400, auto: format }) {
            ${RESPONSIVE_IMAGE}
          }
        }
        externalImageUrl
        externalImageWidth
        externalImageHeight
        authors {
          name
          avatar {
            responsiveImage(imgixParams: { w: 96, h: 96, fit: crop, auto: format }) {
              ${RESPONSIVE_IMAGE}
            }
          }
        }
        categories {
          name: category
        }
        tags {
          name: tag
        }
      }
    }
  `,
    { slug }
  );

  const bp = data.blogPost;
  if (!bp) return null;

  return {
    id: bp.id,
    title: bp.title,
    slug: bp.slug,
    firstPublishedAt: bp.firstPublishedAt,
    updatedAt: bp.updatedAt,
    description: buildExcerpt(bp.description, 160),
    structuredText: bp.structuredText,
    coverImage: bp.featuredImage?.responsiveImage ?? null,
    externalImageUrl: bp.externalImageUrl ?? null,
    externalImageWidth: bp.externalImageWidth ?? null,
    externalImageHeight: bp.externalImageHeight ?? null,
    authors: (bp.authors ?? []).map((a) => ({
      name: a.name,
      avatar: a.avatar?.responsiveImage ?? null,
    })),
    categories: bp.categories ?? [],
    tags: bp.tags ?? [],
    faqs: extractFaqs(bp.structuredText),
  };
}
